// Proven posts to follow: real posts from Inspiration, each with its own picture and numbers.
// Picking one hands its form (the template) and whose it was to the moment box; the host's own
// words and photos still make the post. Nothing here is a placeholder: a post without a picture
// is not offered.
import { useEffect, useRef, useState } from 'react';
import { Overlay } from '@/components/Overlay';
import { offerable, provenStat, type ProvenPost } from '@/lib/proven';
import type { Kind } from '@/lib/wizard';

export type { ProvenPost };

export function ProvenPicker({
  kind,
  load,
  onPick,
  onClose,
}: {
  kind: Kind;
  load: () => Promise<ProvenPost[]>;
  onPick: (post: ProvenPost) => void;
  onClose: () => void;
}) {
  const [posts, setPosts] = useState<ProvenPost[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [dead, setDead] = useState<string[]>([]);
  const dialog = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let live = true;
    load().then(
      (all) => live && setPosts(offerable(all, kind)),
      () => live && setFailed(true),
    );
    return () => {
      live = false;
    };
  }, [load, kind]);
  useEffect(() => {
    dialog.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <Overlay className="pv-backdrop" onClose={onClose}>
      <div
        ref={dialog}
        className="pv-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pv-title"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pv-head">
          <h2 id="pv-title">Like a proven post</h2>
          <button type="button" className="mo-x" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>
        <p className="pv-lede">
          Real {kind === 'Reel' ? 'reels' : 'posts'} from hosts that did well. Pick one and yours
          follows its form, made from your place and your words.
        </p>
        {failed ? (
          <p role="alert" className="pv-empty">
            Proven posts could not load. Your moment is kept; try again in a moment.
          </p>
        ) : posts === null ? (
          <p className="pv-empty" role="status">
            Loading proven posts…
          </p>
        ) : posts.length === 0 ? (
          <p className="pv-empty">No proven {kind === 'Reel' ? 'reels' : 'posts'} to follow yet.</p>
        ) : (
          <ul className="pv-grid" aria-label="Proven posts">
            {posts
              .filter((p) => !dead.includes(p.id))
              .map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    aria-label={`Follow @${p.handle}’s ${p.format === 'Reel' ? 'reel' : 'post'} · ${provenStat(p)}`}
                    onClick={() => onPick(p)}
                  >
                    <img
                      src={p.imageUrl!}
                      alt=""
                      loading="lazy"
                      onError={() => setDead((d) => [...d, p.id])}
                    />
                    <span className="pv-meta">
                      <span className="h">@{p.handle}</span>
                      <span className="n">{provenStat(p)}</span>
                    </span>
                  </button>
                </li>
              ))}
          </ul>
        )}
      </div>
    </Overlay>
  );
}
